import React from 'react';
import { Button } from './Button';

export type ErrorType = 
  | 'api_error'
  | 'location_denied'
  | 'location_unavailable'
  | 'network_error'
  | 'timeout_error'
  | 'rate_limit_error'
  | 'unknown_error';

export interface ErrorStateProps {
  /** Type of error */
  errorType: ErrorType;
  /** Custom error message */
  message?: string;
  /** Detailed error description */
  details?: string;
  /** Whether to show retry button */
  showRetry?: boolean;
  /** Retry button handler */
  onRetry?: () => void;
  /** Retry button text */
  retryText?: string;
  /** Whether to show alternative action button */
  showAlternative?: boolean;
  /** Alternative action handler */
  onAlternative?: () => void;
  /** Alternative action text */
  alternativeText?: string;
  /** Additional CSS classes */
  className?: string;
  /** Test ID for testing */
  'data-testid'?: string;
}

/**
 * ErrorState component for displaying various error states
 * Handles API errors, location errors, and network errors
 * Requirements: 5.3
 */
export const ErrorState: React.FC<ErrorStateProps> = ({
  errorType,
  message,
  details,
  showRetry = true,
  onRetry,
  retryText = '再試行',
  showAlternative = false,
  onAlternative,
  alternativeText = '別の方法を試す',
  className = '',
  'data-testid': testId = 'error-state'
}) => {
  // Get default error content based on error type
  const getErrorContent = () => {
    switch (errorType) {
      case 'location_denied':
        return {
          icon: '📍',
          title: '位置情報の取得が拒否されました',
          defaultMessage: '位置情報の使用を許可してください',
          defaultDetails: 'ブラウザの設定で位置情報の使用を許可し、ページを再読み込みしてください。',
          showLocationHelp: true
        };
      
      case 'location_unavailable':
        return {
          icon: '🌐',
          title: '位置情報を取得できません',
          defaultMessage: '現在地を特定できませんでした',
          defaultDetails: 'GPS信号が弱いか、位置情報サービスが利用できません。しばらく待ってから再試行してください。',
          showLocationHelp: true
        };
      
      case 'api_error':
        return {
          icon: '⚠️',
          title: 'サービスエラー',
          defaultMessage: 'プランの取得に失敗しました',
          defaultDetails: 'サーバーとの通信でエラーが発生しました。しばらく待ってから再試行してください。'
        };
      
      case 'network_error':
        return {
          icon: '🌐',
          title: 'ネットワークエラー',
          defaultMessage: 'インターネット接続を確認してください',
          defaultDetails: 'ネットワーク接続が不安定です。接続を確認してから再試行してください。'
        };
      
      case 'timeout_error':
        return {
          icon: '⏱️',
          title: 'タイムアウトエラー',
          defaultMessage: 'リクエストがタイムアウトしました',
          defaultDetails: 'サーバーの応答に時間がかかりすぎています。しばらく待ってから再試行してください。'
        };
      
      case 'rate_limit_error':
        return {
          icon: '🚫',
          title: 'アクセス制限',
          defaultMessage: 'しばらく時間をおいてから再試行してください',
          defaultDetails: 'アクセス頻度が高すぎます。15分程度待ってから再度お試しください。'
        };
      
      default:
        return {
          icon: '❌',
          title: 'エラーが発生しました',
          defaultMessage: '予期しないエラーが発生しました',
          defaultDetails: 'しばらく待ってから再試行してください。問題が続く場合は、ページを再読み込みしてください。'
        };
    }
  };

  const errorContent = getErrorContent();
  const displayMessage = message || errorContent.defaultMessage;
  const displayDetails = details || errorContent.defaultDetails;

  return (
    <div 
      className={`flex flex-col items-center justify-center p-8 text-center ${className}`}
      data-testid={testId}
      role="alert"
      aria-live="assertive"
    >
      {/* Error icon */}
      <div className="text-4xl mb-4" aria-hidden="true">
        {errorContent.icon}
      </div>

      {/* Error title */}
      <h3 className="text-xl font-semibold text-gray-900 mb-2">
        {errorContent.title}
      </h3>

      {/* Error message */}
      <p className="text-lg text-gray-700 mb-4 max-w-md">
        {displayMessage}
      </p>

      {/* Error details */}
      {displayDetails && (
        <p className="text-sm text-gray-600 mb-6 max-w-lg leading-relaxed">
          {displayDetails}
        </p>
      )}

      {/* Location help for location errors */}
      {errorContent.showLocationHelp && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 max-w-lg">
          <h4 className="font-medium text-blue-900 mb-2">位置情報の許可方法:</h4>
          <ol className="text-sm text-blue-800 text-left space-y-1">
            <li>1. ブラウザのアドレスバー左側の鍵アイコンをクリック</li>
            <li>2. 「位置情報」を「許可」に変更</li>
            <li>3. ページを再読み込み</li>
          </ol>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        {showRetry && onRetry && (
          <Button
            onClick={onRetry}
            variant="primary"
            className="min-w-[120px]"
            data-testid="retry-button"
            aria-label={`${retryText}する`}
          >
            {retryText}
          </Button>
        )}
        
        {showAlternative && onAlternative && (
          <Button
            onClick={onAlternative}
            variant="secondary"
            className="min-w-[120px]"
            data-testid="alternative-button"
            aria-label={alternativeText}
          >
            {alternativeText}
          </Button>
        )}
      </div>

      {/* Additional help text for specific errors */}
      {errorType === 'rate_limit_error' && (
        <p className="text-xs text-gray-500 mt-4 max-w-md">
          このサービスは無料で提供されており、サーバー負荷軽減のため利用制限があります。
          ご理解とご協力をお願いします。
        </p>
      )}

      {/* Screen reader only text */}
      <div className="sr-only">
        エラーが発生しました。{errorContent.title}。{displayMessage}
        {showRetry && onRetry && '再試行ボタンで再度お試しいただけます。'}
      </div>
    </div>
  );
};

export default ErrorState;